// @flow
import { Button, TableColumn } from '@vadiun/react-components';
import * as React from 'react';
import { Table } from 'shared/components';
import { createDomicilioText } from 'shared/models/DomicilioGoogle';
import { TransferReservationTableTimesCell } from '../../viajeTraslado/components/TransferReservationTableTimesCell';
import { TransferReservationManagmentModel } from '../models/TransferReservationManagment';
import ServiceType from '../../coordinar/components/list/serviceType';
import { GestionDeleteColumnVisibility } from '../utils/gestionDeleteStorage';

interface TableQueryData {
  data: TransferReservationManagmentModel[];
  total: number;
  isLoading: boolean;
  error: Error | undefined;
  serverSideOptions: any;
}

interface Props {
  tableQuery: TableQueryData;
  onLocationsDetails: (reservation: TransferReservationManagmentModel) => void;
  onDelete: (id: number) => React.ReactNode;
  handlePageChange: (currentPage: number) => void;
  columnVisibility: GestionDeleteColumnVisibility;
  onChangePage: (newPage: number) => void;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
}

export function ViajeTrasladoDeleteTable({
  tableQuery,
  onLocationsDetails,
  onDelete,
  handlePageChange,
  columnVisibility,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange
}: Props) {
  const data = (tableQuery.data ?? []).map((reservation) => ({
    ...reservation,
    schedule: {
      arrival: reservation.reservationDatetime,
      exit: reservation.departureDatetime,
      end: reservation.arrivalDateTime
    }
  }));

  const columns: TableColumn<any>[] = [
    {
      name: 'schedule',
      label: 'Horarios',
      options: {
        display: columnVisibility.schedule ? 'true' : 'excluded',
        customBodyRender: (schedule) => {
          return <TransferReservationTableTimesCell schedule={schedule} />;
        }
      }
    },
    {
      name: 'cliente',
      label: 'Cliente',
      options: {
        display: columnVisibility.cliente ? 'true' : 'excluded',
        customBodyRenderLite: (index) => (
          <span
            className={
              !data[index].pay_with_cash ? 'text-blue-500' : 'text-black'
            }
          >
            {data[index].client.name}
          </span>
        )
      }
    },
    {
      name: 'puntos',
      label: 'Domicilios',
      options: {
        display: columnVisibility.puntos ? 'true' : 'excluded',
        customBodyRenderLite: (index) => {
          return (
            <div className="flex flex-col items-start">
              {data[index].locations.map((location, index) => (
                <div
                  key={`${data[index]}-location-${index}`}
                  className="mb-1 flex items-start"
                >
                  <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-500 p-1 text-white">
                    {index + 1}
                  </div>
                  <span className="ml-1">
                    {createDomicilioText(location.address)}
                  </span>
                </div>
              ))}
              <Button
                shape="circle"
                className="w-full p-2"
                onClick={() => onLocationsDetails(data[index])}
                color="blue"
              >
                <div className="flex items-center gap-1">Info</div>
              </Button>
            </div>
          );
        }
      }
    },
    {
      name: 'coordinationObservation',
      label: 'Obs. Coordinación',
      options: {
        display: columnVisibility.coordinationObservation ? 'true' : 'excluded'
      }
    },
    {
      name: 'serviceType',
      label: 'Servicio',
      options: {
        display: columnVisibility.serviceType ? 'true' : 'excluded',
        customBodyRender: (value, { rowIndex: index }) => (
          <ServiceType data={data} index={index} isMobileOrTablet={false} />
        )
      }
    },
    {
      name: 'id',
      label: 'ID',
      options: {
        display: columnVisibility.id ? 'true' : 'excluded'
      }
    },
    {
      name: 'platformId',
      label: 'Plataforma',
      options: {
        display: columnVisibility.platformId ? 'true' : 'excluded'
      }
    },
    {
      name: 'assigned_driver',
      label: 'Chofer',
      options: {
        display: columnVisibility.assigned_driver ? 'true' : 'excluded',
        customBodyRenderLite: (index) => (
          <div className="flex flex-col items-center">
            <p>{data[index].assigned_driver?.codigo}</p>
            <p className="text-center text-xs">
              {data[index].assigned_driver?.nombreCompleto}
            </p>
          </div>
        )
      }
    },
    {
      name: 'state',
      label: 'Estado',
      options: {
        display: columnVisibility.state ? 'true' : 'excluded'
      }
    },
    {
      label: 'Acciones',
      name: 'id',
      options: {
        display: columnVisibility.actions ? 'true' : 'excluded',
        customBodyRenderLite: (index) => (
          <div className="flex space-x-2">{onDelete(data[index].id)}</div>
        )
      }
    }
  ];

  return (
    <div className="w-full">
      <Table
        title={''}
        data={data}
        options={{
          elevation: 0,
          selectableRows: 'none',
          search: false,
          filter: false,
          sort: false,
          viewColumns: false,
          serverSide: true,
          download: false,
          print: false,
          onChangePage(page: number) {
            onChangePage(page + 1); // MUI DataTable usa índice base 0, convertir a base 1
            handlePageChange(page + 1); // Para el scroll
          },
          onChangeRowsPerPage(rowsPerPage: number) {
            onChangeRowsPerPage(rowsPerPage);
          },
          onColumnSortChange: (
            changedColumn: string,
            direction: 'asc' | 'desc'
          ) => {
            onSortChange(direction, changedColumn);
          },
          ...tableQuery.serverSideOptions
        }}
        columns={columns}
      />
    </div>
  );
}
